import { useMemo, useState } from 'react';
import { useGameContext } from '@/state/GameContext';
import { RARITY_COLORS } from '@/state/settings';

export function DiscoveryGraph() {
  const game = useGameContext();
  const [selected, setSelected] = useState<string | null>(null);
  const items = game.discovered;

  const layout = useMemo(() => {
    // order by discovery (oldest first) for a left-to-right flow
    const ordered = [...items].sort((a, b) => a.discoveredAt - b.discoveredAt);
    const colW = 130;
    const rowH = 90;
    const byId = new Map(ordered.map((d) => [d.id, d]));
    const nodes = ordered.map((d, i) => ({
      id: d.id,
      item: d,
      x: 40 + (i % 6) * colW,
      y: 40 + d.tier * rowH,
    }));
    const edges: Array<{ from: string; to: string }> = [];
    for (const child of ordered) {
      for (const pid of child.ingredientIds) {
        if (byId.has(pid)) edges.push({ from: pid, to: child.id });
      }
    }
    const width = Math.max(600, ordered.length * (colW / 6) + colW);
    const height = 6 * rowH + 60;
    return { nodes, edges, width, height };
  }, [items]);

  if (items.length === 0) {
    return <div className="panel-body muted">Discover custom items to see their lineage graph.</div>;
  }

  const nodeById = new Map(layout.nodes.map((n) => [n.id, n]));
  const sel = selected ? items.find((d) => d.id === selected) : null;

  return (
    <div className="panel-body" style={{ padding: 0, position: 'relative' }}>
      <svg className="graph-svg" viewBox={`0 0 ${layout.width} ${layout.height}`} preserveAspectRatio="xMinYMin meet" style={{ minHeight: 360 }}>
        {layout.edges.map((e, i) => {
          const a = nodeById.get(e.from);
          const b = nodeById.get(e.to);
          if (!a || !b) return null;
          return <line key={i} className="graph-edge" x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
        })}
        {layout.nodes.map((n) => {
          const color = RARITY_COLORS[n.item.rarity] || '#fff';
          return (
            <g key={n.id} className="graph-node" onClick={() => setSelected(n.id)} style={{ cursor: 'pointer' }}>
              <circle cx={n.x} cy={n.y} r={selected === n.id ? 16 : 11} fill={color} opacity={selected && selected !== n.id ? 0.4 : 0.9} />
              <text className="graph-label" x={n.x} y={n.y + 28} textAnchor="middle">{n.item.name.slice(0, 14)}</text>
            </g>
          );
        })}
      </svg>
      {sel ? (
        <div style={{ position: 'absolute', bottom: 8, left: 8, right: 8, background: '#1f1f22', border: '1px solid #333', padding: 10, fontSize: 12 }}>
          <b style={{ color: RARITY_COLORS[sel.rarity] || '#fff' }}>{sel.name}</b>
          <div className="muted">Tier {sel.tier} &middot; {sel.category} &middot; {sel.ingredientIds.length} ingredients</div>
          <div className="muted">{sel.description}</div>
        </div>
      ) : (
        <div className="muted" style={{ position: 'absolute', top: 8, left: 10 }}>Click a node to inspect.</div>
      )}
    </div>
  );
}
